/**
 * Park Manager Portal Dashboard
 * Mobile-first dashboard showing open jobs, in progress, completed, and PPM status
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ClipboardList, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  Calendar,
  Plus,
  ChevronRight,
  Home
} from 'lucide-react';
import { toast } from 'sonner';
import { PortalLayout } from '../components/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wyldecrest Stabilised Workflow - 8 Stages Only
const STATUS_COLORS = {
  'Job Created': 'bg-blue-100 text-blue-700',
  'Awaiting Quote': 'bg-purple-100 text-purple-700',
  'Quote Awaiting Approval': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-green-100 text-green-700',
  'Assigned': 'bg-cyan-100 text-cyan-700',
  'Scheduled': 'bg-teal-100 text-teal-700',
  'Completed': 'bg-emerald-100 text-emerald-700',
  'Closed': 'bg-slate-100 text-slate-600',
};

const PRIORITY_COLORS = {
  'Urgent': 'bg-red-600 text-white',
  'High': 'bg-orange-500 text-white',
  'Medium': 'bg-blue-500 text-white',
  'Low': 'bg-slate-400 text-white',
};

export default function PortalDashboardPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/park-portal/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch dashboard');
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
        </div>
      </PortalLayout>
    );
  }

  const parkName = dashboard?.parks?.[0]?.name || 'Your Park';

  return (
    <PortalLayout>
      <div className="p-4 pb-20 md:p-6 md:pb-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-[#1F7A78] mb-1">
            <Home className="h-4 w-4" />
            <span className="text-sm font-medium">{parkName}</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Dashboard</h1>
        </div>

        {/* Quick Action */}
        <Button 
          onClick={() => navigate('/portal/report')}
          className="w-full bg-[#1F7A78] hover:bg-[#005a5c] mb-6 h-12 text-base"
          data-testid="report-issue-btn"
        >
          <Plus className="h-5 w-5 mr-2" />
          Report New Issue
        </Button>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Card 
            className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/portal/jobs?status=open')}
            data-testid="open-jobs-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <ClipboardList className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.jobs?.open || 0}</p>
                  <p className="text-xs text-slate-500">Open Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/portal/jobs?status=in_progress')}
            data-testid="in-progress-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-50 rounded-lg">
                  <Clock className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.jobs?.in_progress || 0}</p>
                  <p className="text-xs text-slate-500">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/portal/jobs?status=completed')}
            data-testid="completed-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.jobs?.completed_last_30_days || 0}</p>
                  <p className="text-xs text-slate-500">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-red-500"
            onClick={() => navigate('/portal/ppm?status=overdue')}
            data-testid="ppm-overdue-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{dashboard?.ppm?.overdue || 0}</p>
                  <p className="text-xs text-slate-500">PPM Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* PPM Due Soon */}
        <Card className="border-0 shadow-sm mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-orange-500" />
                PPM Due (30 Days)
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/portal/ppm')}
                className="text-[#1F7A78]"
              >
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-orange-600">{dashboard?.ppm?.due_next_30_days || 0}</p>
              <p className="text-sm text-slate-500">Tasks due in next 30 days</p>
            </div>
          </CardContent>
        </Card>

        {/* Recent Jobs */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent Jobs</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/portal/jobs')}
                className="text-[#1F7A78]"
              >
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              {dashboard?.jobs?.recent?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.jobs.recent.map((job) => (
                    <div
                      key={job.id}
                      className="p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => navigate(`/portal/jobs?selected=${job.id}`)}
                      data-testid={`job-card-${job.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{job.job_reference}</span>
                        <Badge className={`text-xs ${STATUS_COLORS[job.status] || 'bg-slate-100'}`}>
                          {job.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-600 truncate">{job.description || job.job_type_label}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {job.unit_pitch && (
                          <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{job.unit_pitch}</span>
                        )}
                        <Badge className={`text-xs ${PRIORITY_COLORS[job.priority] || 'bg-slate-400 text-white'}`}>
                          {job.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent jobs</p>
                  <Button 
                    className="mt-3 bg-[#1F7A78]" 
                    size="sm"
                    onClick={() => navigate('/portal/report')}
                  >
                    Report First Issue
                  </Button>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
